/**
 * CLI Documentation Management
 *
 * Allows agents and superadmins to list, pull, edit, and push documentation pages.
 * Supports both global (public/superadmin) and org-scoped (--org-scoped) modes.
 */

import { readFile, writeFile, mkdir } from 'node:fs/promises';
import path from 'node:path';
import { getSubDir } from '@chatsdet/tmp';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type DocsPageListItem = {
  slug: string;
  title: string;
  description: string | null;
  updatedAt: string;
  publishedBy: string | null;
};

type DocsVersionItem = {
  version: number;
  title: string;
  checksum: string;
  message: string | null;
  createdBy: string;
  createdAt: string;
};

type DocsApiResponse = {
  ok: boolean;
  error?: string;
  data?: {
    slug?: string;
    title?: string;
    pages?: DocsPageListItem[];
    content?: string;
    versions?: DocsVersionItem[];
  };
};

const DEFAULT_PULL_DIR = getSubDir('docs');

/**
 * Parse the optional data-driven sidebar-placement flags. Only present flags
 * are included, so `push`/`create` without them leaves existing values intact.
 */
function parseNavFields(argv: string[]): {
  navSection?: string;
  navGroup?: string;
  navOrder?: number;
} {
  const out: { navSection?: string; navGroup?: string; navOrder?: number } = {};
  const section = getArgValue(argv, '--section');
  const group = getArgValue(argv, '--group');
  const order = getArgValue(argv, '--order');
  if (section !== null && section !== undefined) out.navSection = section;
  if (group !== null && group !== undefined) out.navGroup = group;
  if (order !== null && order !== undefined) {
    const n = Number.parseInt(order, 10);
    if (Number.isNaN(n)) {
      console.error('Error: --order must be an integer.');
      process.exit(1);
    }
    out.navOrder = n;
  }
  return out;
}

/** Return the base path prefix for read endpoints based on --org-scoped flag */
function readPrefix(orgMode: boolean): string {
  return orgMode ? '/docs' : '/public/docs';
}

/** Return the base path prefix for write endpoints based on --org-scoped flag */
function writePrefix(orgMode: boolean): string {
  return orgMode ? '/docs' : '/superadmin/docs';
}

async function handleList(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const result = await apiRequest<DocsApiResponse>(apiUrl, token, 'GET', `${readPrefix(orgMode)}/pages`);
  const pages: DocsPageListItem[] = result.data?.pages ?? [];

  if (jsonOutput) {
    console.log(JSON.stringify(pages, null, 2));
    return;
  }

  if (pages.length === 0) {
    console.log('No documentation pages found.');
    return;
  }

  // Default: markdown table for easy consumption by agents and humans
  const slugWidth = Math.max(4, ...pages.map((p) => p.slug.length));
  const titleWidth = Math.max(5, ...pages.map((p) => p.title.length));
  const header = `| ${'Slug'.padEnd(slugWidth)} | ${'Title'.padEnd(titleWidth)} | Updated    | Published By |`;
  const sep = `| ${'-'.repeat(slugWidth)} | ${'-'.repeat(titleWidth)} | ---------- | ------------ |`;
  console.log(header);
  console.log(sep);
  for (const page of pages) {
    const date = new Date(page.updatedAt).toISOString().slice(0, 10);
    const by = page.publishedBy ?? '';
    console.log(`| ${page.slug.padEnd(slugWidth)} | ${page.title.padEnd(titleWidth)} | ${date} | ${by.padEnd(12)} |`);
  }
}

async function fetchDocContent(slug: string, apiUrl: string, token: string, orgMode: boolean): Promise<string> {
  if (orgMode) {
    const result = await apiRequest<DocsApiResponse>(
      apiUrl,
      token,
      'GET',
      `/docs/pages/content?slug=${encodeURIComponent(slug)}`
    );
    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      process.exit(1);
    }
    return result.data?.content ?? '';
  }

  const res = await fetch(`${apiUrl}/public/docs/pages/raw?slug=${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) {
    console.error(`Error: Page not found: ${slug}`);
    process.exit(1);
  }

  if (!res.ok) {
    console.error(`Error: ${res.statusText}`);
    process.exit(1);
  }

  return res.text();
}

async function handleGet(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs get <slug>');
    process.exit(1);
  }

  const content = await fetchDocContent(slug, apiUrl, token, orgMode);
  console.log(content);
}

async function handlePull(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs pull <slug> [--output <path>]');
    process.exit(1);
  }

  const outputPath = getArgValue(argv, '--output') ?? path.join(DEFAULT_PULL_DIR, `${slug}.md`);
  const content = await fetchDocContent(slug, apiUrl, token, orgMode);

  await mkdir(path.dirname(outputPath), { recursive: true });
  await writeFile(outputPath, content, 'utf8');
  console.log(`Pulled ${slug} → ${outputPath}`);
}

async function handlePush(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs push <slug> --file <path> [--message <text>]');
    process.exit(1);
  }

  const filePath = getArgValue(argv, '--file');
  if (!filePath) {
    console.error('Error: Missing --file <path>.');
    process.exit(1);
  }

  const message = getArgValue(argv, '--message') ?? undefined;
  const createdBy = getArgValue(argv, '--created-by') ?? undefined;

  let content: string;
  try {
    content = await readFile(filePath, 'utf8');
  } catch {
    console.error(`Error: Could not read file: ${filePath}`);
    process.exit(1);
  }

  // Strip `export const meta = { ... };` block so raw JS isn't stored as doc content
  content = content.replace(/^\s*export\s+const\s+meta\s*=\s*\{[^}]*\};\s*/, '');

  const result = await apiRequest<DocsApiResponse>(
    apiUrl,
    token,
    'PUT',
    `${writePrefix(orgMode)}/pages?slug=${encodeURIComponent(slug)}`,
    { content, message, createdBy, ...parseNavFields(argv) }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Published new version of ${slug}`);
}

async function handleHistory(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs history <slug>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');

  const res = await fetch(`${apiUrl}${readPrefix(orgMode)}/pages/versions?slug=${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    console.error(`Error: ${(body as { error?: string }).error ?? res.statusText}`);
    process.exit(1);
  }

  const body = (await res.json()) as DocsApiResponse;
  const versions = body.data?.versions ?? [];

  if (jsonOutput) {
    console.log(JSON.stringify(versions, null, 2));
    return;
  }

  if (versions.length === 0) {
    console.log(`No versions found for ${slug}.`);
    return;
  }

  for (const v of versions) {
    const date = new Date(v.createdAt).toISOString().slice(0, 19).replace('T', ' ');
    const msg = v.message ? ` — ${v.message}` : '';
    console.log(`  v${v.version}  ${date}  ${v.createdBy}${msg}`);
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs create <slug> --file <path> --title <text>');
    process.exit(1);
  }

  const filePath = getArgValue(argv, '--file');
  const title = getArgValue(argv, '--title');

  if (!filePath) {
    console.error('Error: Missing --file <path>.');
    process.exit(1);
  }
  if (!title) {
    console.error('Error: Missing --title <text>.');
    process.exit(1);
  }

  const description = getArgValue(argv, '--description') ?? undefined;
  const message = getArgValue(argv, '--message') ?? undefined;

  let content: string;
  try {
    content = await readFile(filePath, 'utf8');
  } catch {
    console.error(`Error: Could not read file: ${filePath}`);
    process.exit(1);
  }

  // Strip `export const meta = { ... };` block so raw JS isn't stored as doc content
  content = content.replace(/^\s*export\s+const\s+meta\s*=\s*\{[^}]*\};\s*/, '');

  const result = await apiRequest<DocsApiResponse>(
    apiUrl,
    token,
    'POST',
    `${writePrefix(orgMode)}/pages`,
    {
      slug,
      title,
      description,
      content,
      message,
      ...parseNavFields(argv),
    }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Created page: ${slug}`);
}

type DocsSearchResult = {
  path: string;
  title: string;
  description: string | null;
  excerpt: string;
};

type DocsSearchResponse = {
  ok: boolean;
  error?: string;
  data?: { results: DocsSearchResult[] };
};

async function handleSearch(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const query = argv.filter((a) => !a.startsWith('--')).join(' ').trim();
  if (!query) {
    console.error('Error: Missing search query.');
    console.error('Usage: canary docs search <query> [--limit <n>] [--json]');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const limitArg = getArgValue(argv, '--limit');
  const params = new URLSearchParams({ q: query });
  if (limitArg) params.set('limit', limitArg);

  const searchPath = orgMode ? '/docs/search' : '/public/docs/search';
  const res = await fetch(`${apiUrl}${searchPath}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    console.error(`Error: ${(body as { error?: string }).error ?? res.statusText}`);
    process.exit(1);
  }

  const body = (await res.json()) as DocsSearchResponse;
  const results = body.data?.results ?? [];

  if (jsonOutput) {
    console.log(JSON.stringify(results, null, 2));
    return;
  }

  if (results.length === 0) {
    console.log('No results found.');
    return;
  }

  for (const r of results) {
    const desc = r.description ? `  ${r.description}` : '';
    const excerpt = r.excerpt
      ? `  ${r.excerpt.replace(/<\/?mark>/g, '').replace(/\n/g, ' ').slice(0, 120)}`
      : '';
    console.log(`  ${r.path ?? r.title}`);
    if (r.title) console.log(`    ${r.title}${desc}`);
    if (excerpt) console.log(`   ${excerpt}`);
    console.log();
  }
}

async function handleDelete(argv: string[], apiUrl: string, token: string, orgMode: boolean): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: canary docs delete <slug>');
    process.exit(1);
  }

  const result = await apiRequest<DocsApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `${writePrefix(orgMode)}/pages?slug=${encodeURIComponent(slug)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Deleted page: ${slug}`);
}

type DocsNavGroup = { key: string; title: string; sortOrder: number };

async function handleNav(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;

  switch (action) {
    case undefined:
    case 'list': {
      const result = await apiRequest<{ ok: boolean; data?: { groups: DocsNavGroup[] }; error?: string }>(
        apiUrl,
        token,
        'GET',
        '/public/docs/nav'
      );
      const groups = result.data?.groups ?? [];
      if (hasFlag(rest, '--json')) {
        console.log(JSON.stringify(groups, null, 2));
        return;
      }
      if (groups.length === 0) {
        console.log('No sidebar groups found.');
        return;
      }
      const keyWidth = Math.max(3, ...groups.map((g) => g.key.length));
      const titleWidth = Math.max(5, ...groups.map((g) => g.title.length));
      console.log(`| ${'Key'.padEnd(keyWidth)} | ${'Title'.padEnd(titleWidth)} | Order |`);
      console.log(`| ${'-'.repeat(keyWidth)} | ${'-'.repeat(titleWidth)} | ----- |`);
      for (const g of groups) {
        console.log(`| ${g.key.padEnd(keyWidth)} | ${g.title.padEnd(titleWidth)} | ${String(g.sortOrder).padEnd(5)} |`);
      }
      return;
    }
    case 'set': {
      const key = rest[0];
      if (!key || key.startsWith('--')) {
        console.error('Usage: canary docs nav set <key> [--title <text>] [--order <n>]');
        process.exit(1);
      }
      const title = getArgValue(rest, '--title');
      const orderRaw = getArgValue(rest, '--order');
      const body: Record<string, unknown> = {};
      if (title !== null && title !== undefined) body.title = title;
      if (orderRaw !== null && orderRaw !== undefined) {
        const n = Number.parseInt(orderRaw, 10);
        if (Number.isNaN(n)) {
          console.error('Error: --order must be an integer.');
          process.exit(1);
        }
        body.sortOrder = n;
      }
      await apiRequest(apiUrl, token, 'PUT', `/superadmin/docs/nav?key=${encodeURIComponent(key)}`, body);
      console.log(`Updated sidebar group: ${key}`);
      return;
    }
    case 'reorder': {
      const keys = rest.filter((a) => !a.startsWith('--'));
      if (keys.length === 0) {
        console.error('Usage: canary docs nav reorder <key1> <key2> ...');
        process.exit(1);
      }
      await apiRequest(apiUrl, token, 'PUT', '/superadmin/docs/nav/reorder', { keys });
      console.log(`Reordered ${keys.length} sidebar groups.`);
      return;
    }
    case 'delete': {
      const key = rest[0];
      if (!key || key.startsWith('--')) {
        console.error('Usage: canary docs nav delete <key>');
        process.exit(1);
      }
      await apiRequest(apiUrl, token, 'DELETE', `/superadmin/docs/nav?key=${encodeURIComponent(key)}`);
      console.log(`Deleted sidebar group: ${key}`);
      return;
    }
    default:
      console.error(`Unknown nav action: ${action}`);
      console.error('Actions: list, set <key>, reorder <keys...>, delete <key>');
      process.exit(1);
  }
}

function printDocsHelp(): void {
  console.log(
    [
      'Usage: canary docs <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list                                    List all doc pages',
      '  get <slug>                              Print content to stdout',
      '  search <query>                          Full-text search docs',
      '  pull <slug> [--output <path>]           Download to local file',
      '  push <slug> --file <path> [--message]   Publish new version',
      '  history <slug>                          Show version history',
      '  create <slug> --file <path> --title ... Create new page',
      '  delete <slug>                           Soft-delete a page',
      '  nav list [--json]                       List sidebar groups + order',
      '  nav set <key> [--title <t>] [--order n] Create or update a sidebar group',
      '  nav reorder <key1> <key2> ...           Set sidebar group order',
      '  nav delete <key>                        Remove a sidebar group',
      '',
      'Options:',
      '  --org-scoped           Use org-scoped endpoints (requires auth)',
      '  --file <path>          Path to markdown file',
      '  --title <text>         Page title (create only)',
      '  --description <text>   Page description (create only)',
      '  --group <key>          Sidebar group key the page belongs to (see `nav list`; create/push)',
      '  --order <n>            Sort order within the group (create/push)',
      '  --section <text>       Deprecated; sidebar grouping is driven by --group now',
      '  --message <text>       Version commit message',
      '  --limit <n>            Max results for search (default: 8, max: 20)',
      '  --output <path>        Output path for pull (default: <tmpdir>/canary/docs/<slug>.md)',
      '  --json                 Output as JSON (list, history, search)',
      '  --env <env>            Target environment (prod, dev)',
      '  --api-url <url>        API URL override',
      '  --token <key>          API token override',
    ].join('\n')
  );
}

export async function runDocs(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printDocsHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);
  // `--org` is the global identity selector now; docs uses `--org-scoped` to
  // switch to org-scoped endpoints.
  const orgMode = hasFlag(rest, '--org-scoped');

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token, orgMode);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token, orgMode);
      break;
    case 'search':
      await handleSearch(rest, apiUrl, token, orgMode);
      break;
    case 'pull':
      await handlePull(rest, apiUrl, token, orgMode);
      break;
    case 'push':
      await handlePush(rest, apiUrl, token, orgMode);
      break;
    case 'history':
      await handleHistory(rest, apiUrl, token, orgMode);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token, orgMode);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token, orgMode);
      break;
    case 'nav':
      await handleNav(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printDocsHelp();
      process.exit(1);
  }
}
